import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { API } from 'constants';
import client from '../../../services/template-api';

import { useFormState } from '../../../hooks/useFormState';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useSwrRequestKeysToMutateFromContext } from '../../../hooks/useSwrRequestKeysToMutateFromContext';

import { capitalizeStr } from '../../../helpers/formatOrganizationDataForTable';
import { megabytesToBytes } from '../../../helpers/fileUtils';
import { mutateListDataAfterUpdatingItem } from '../../../helpers/swrMutateUtils';

import { collectionColorOptions } from '../../../constants/collections';
import { defaultErrorMessage, emptyFieldErrorMessage } from '../../../constants/errorMessages';

import Button from '../../../design-system/Button/Button';
import FileInput from '../../../design-system/FileInput/FileInput';
import ColorPicker from '../../../design-system/ColorPicker/ColorPicker';
import LabelsSection from '../PlaybookTemplateDetailPage/LabelsSection/LabelsSection';
import PlayProcessIcon from '../../../design-system/Icons/PlayProcessIcon';
import FormFieldWrapper from '../../../design-system/FormFieldWrapper/FormFieldWrapper';
import CreationPageContainer from '../../../design-system/CreationPageContainer/CreationPageContainer';
import { Input, TextArea } from '../../../design-system';

const maxSizeInBytes = megabytesToBytes(5);

const PlaybookTemplateEditorPage = () => {
    const { playbookTemplateId } = useParams();

    const navigate = useNavigate();
    const location = useLocation();

    const prevLocation = location.state?.templatesLibraryLocation;
    const prevLocationIsLibrary = prevLocation?.pathname?.includes('/templates/playbooks');

    const backLinkHref = prevLocationIsLibrary ? prevLocation : '/templates/playbooks';

    const mode = !!playbookTemplateId ? 'edit' : 'create';

    const { formData, setFormData, handleInputChange, fieldErrorMessages, setFieldErrorMessages } =
        useFormState(() => {
            if (mode === 'create') {
                return {
                    name: '',
                    description: '',
                    labels: [],
                    icon_color: collectionColorOptions[0],
                    cover_image: null,
                };
            }
            return null;
        });

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    const {
        requestKeysToMutate: { playbookTemplates: requestKeysToMutate },
    } = useSwrRequestKeysToMutateFromContext();

    const allRequiredFieldsFilled =
        !!formData?.name && !!formData?.description && !!formData?.icon_color;

    useEffect(() => {
        const fetchPlaybookTemplateData = async () => {
            try {
                const { data } = await client.get(
                    API.ROUTES.template.playbookTemplate + playbookTemplateId + '/'
                );
                const { name, description, labels, icon_color, cover_image } = data;
                setFormData({ name, description, labels, icon_color, cover_image });
            } catch (e) {
                navigate(backLinkHref);
            }
        };

        if (mode === 'edit') {
            void fetchPlaybookTemplateData();
        }
    }, []);

    useDocumentTitle(`${capitalizeStr(mode)} Playbook Template`);

    const setLabels = (cb) => {
        setFormData((prevData) => ({
            ...prevData,
            labels: typeof cb === 'function' ? cb(prevData.labels || []) : cb,
        }));
    };

    const getRequestBody = () => {
        const isCoverImageChanged =
            formData.cover_image && typeof formData.cover_image !== 'string';

        if (!isCoverImageChanged) {
            const { cover_image, ...restFormData } = formData;
            return restFormData;
        }

        if (isCoverImageChanged) {
            const requestFormData = new FormData();

            requestFormData.append('name', formData.name);
            requestFormData.append('description', formData.description);
            requestFormData.append('icon_color', formData.icon_color);
            requestFormData.append('cover_image', formData.cover_image);

            const stringifiedLabels = JSON.stringify(formData.labels);
            requestFormData.append('labels', stringifiedLabels);

            return requestFormData;
        }
    };

    const handleCreate = async (requestBody) => {
        const { data } = await client.post(API.ROUTES.template.playbookTemplate, requestBody);

        const templatesLibraryLocation = location.state?.templatesLibraryLocation || null;
        navigate(`/templates/playbook/${data.id}`, { state: { from: templatesLibraryLocation } });
    };

    const handleEdit = async (requestBody) => {
        const { data } = await client.patch(
            API.ROUTES.template.playbookTemplate + playbookTemplateId + '/',
            requestBody
        );

        const { name, description, labels, icon_color, cover_image, updated_at } = data;
        mutateListDataAfterUpdatingItem({
            requestKeysToMutate,
            id: playbookTemplateId,
            updatedPart: { name, description, labels, icon_color, cover_image, updated_at },
        });
        navigate(backLinkHref);
    };

    const handleSubmit = async () => {
        try {
            if (!allRequiredFieldsFilled) {
                setFieldErrorMessages({
                    name: !formData.name ? emptyFieldErrorMessage : null,
                    description: !formData.description ? emptyFieldErrorMessage : null,
                    icon_color: !formData.icon_color ? 'Please select a header color' : null,
                });
                return;
            }

            setIsLoading(true);
            const requestBody = getRequestBody();

            const action = {
                create: handleCreate,
                edit: handleEdit,
            }[mode];

            await action(requestBody);
        } catch (e) {
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        } finally {
            setIsLoading(false);
        }
    };

    const title = {
        create: 'Create New Playbook Template',
        edit: 'Edit Playbook Template',
    }[mode];

    const submitButtonText = {
        create: 'Create Template',
        edit: 'Save Changes',
    }[mode];

    const submitButtonState = isLoading
        ? 'loading'
        : allRequiredFieldsFilled
        ? 'default'
        : 'disabled';

    return (
        <CreationPageContainer
            title={title}
            backButtonText="Back to Library"
            handleBackButtonClick={() => navigate(backLinkHref)}
            errorAlert={errorAlert}
            setErrorAlert={setErrorAlert}
            isLoading={!formData}
        >
            {formData && (
                <>
                    <div className="flex flex-col gap-5 mb-8">
                        <Input
                            size="md"
                            name="playbook_template_name"
                            value={formData.name}
                            label="Name"
                            isRequired
                            placeholder="Give this playbook template a helpful name."
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            state={fieldErrorMessages.name ? 'error' : 'default'}
                            errorMessage={fieldErrorMessages.name}
                        />
                        <TextArea
                            name="description"
                            value={formData.description}
                            label="Description"
                            isRequired
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            state={fieldErrorMessages.description ? 'error' : 'default'}
                            errorMessage={fieldErrorMessages.description}
                            placeholder="Describe in a sentence or two what this playbook does."
                        />
                        <ColorPicker
                            label="Select a header color"
                            isRequired
                            options={collectionColorOptions}
                            selected={formData.icon_color}
                            handleSelect={(color) => handleInputChange('icon_color', color)}
                            gap={4}
                        />
                        <FormFieldWrapper label="Cover Image">
                            <FileInput
                                handleFile={(file) => handleInputChange('cover_image', file)}
                                file={formData.cover_image}
                                formatsArray={['.jpg', '.jpeg', '.png']}
                                maxSizeInBytes={maxSizeInBytes}
                                placeholder="Drag & drop a file to upload or click to browse"
                                shouldShowPreview
                            />
                        </FormFieldWrapper>
                        <FormFieldWrapper label="Add labels" gap={8}>
                            <LabelsSection
                                labels={formData.labels}
                                setLabels={setLabels}
                                isEditable
                                addButtonType="neutral"
                            />
                        </FormFieldWrapper>
                    </div>

                    <Button
                        type="primary"
                        size="sm"
                        text={submitButtonText}
                        trailingIcon={mode === 'create' ? PlayProcessIcon : null}
                        state={submitButtonState}
                        onClick={handleSubmit}
                    />
                </>
            )}
        </CreationPageContainer>
    );
};

export default PlaybookTemplateEditorPage;
